import { createContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import api from "../api/client";
import useFSTLocation from "../hooks/useFSTLocation";

export const AuthContext = createContext(null);

// Track user activity — update last_seen via /me every 5 minutes if active
const ACTIVITY_PING_INTERVAL = 5 * 60 * 1000; // 5 minutes

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const lastActivityRef = useRef(Date.now());

  // Track user activity events
  useEffect(() => {
    if (!user) return;
    const onActivity = () => { lastActivityRef.current = Date.now(); };
    const events = ["mousedown", "keydown", "scroll", "touchstart", "mousemove"];
    events.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));
    return () => {
      events.forEach((evt) => window.removeEventListener(evt, onActivity));
    };
  }, [user]);

  // Periodically call /me to keep last_seen_at updated on backend (only if active)
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(async () => {
      // Only ping if user had activity in the last interval
      if (Date.now() - lastActivityRef.current < ACTIVITY_PING_INTERVAL) {
        try {
          await api.get("/me");
        } catch {
          // 401 interceptor will handle redirect
        }
      }
    }, ACTIVITY_PING_INTERVAL);
    return () => clearInterval(interval);
  }, [user]);

  const fetchUser = useCallback(async () => {
    try {
      const { data } = await api.get("/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (username, password, force = false) => {
    const { data } = await api.post("/login", { username, password, force });
    const { must_change_password, ...userData } = data;
    setUser(userData);
    setMustChangePassword(!!must_change_password);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/logout");
    } catch {
      // Ignore network errors — clear local session regardless
    } finally {
      setUser(null);
      setMustChangePassword(false);
    }
  };

  const clearMustChangePassword = () => setMustChangePassword(false);

  // Build a Set of permission keys from the user's role
  const permissions = useMemo(
    () => new Set(user?.permissions ?? []),
    [user?.permissions]
  );

  /**
   * Check if the current user has a specific permission key.
   * e.g. hasPerm("jo.create"), hasPerm("field.edit.call_status")
   */
  const hasPerm = useCallback((key) => permissions.has(key), [permissions]);

  // Legacy role flags (kept for convenience)
  const role = user?.role ?? null;
  const isAdmin      = role === "admin";
  const isSupervisor = role === "supervisor";
  const isQA         = role === "qa";
  const isSA         = role === "sa";
  const isCCA        = role === "cca";
  const isViewer     = role === "viewer";

  // Sales Agent detection: legacy "sa" role OR RBAC role named "Sales Agent/TL" or "Sales Agent"
  const isSalesAgent = isSA || (user?.role_name === "Sales Agent/TL") || (user?.role_name === "Sales Agent");

  // FST (Field Service Technician) detection
  const isFST = user?.role_name === "FST";
  const isFSTMig = user?.role_name === "FST Migration";

  // Any mobile-only role (used for routing)
  const isMobileRole = isSalesAgent || isFST || isFSTMig;

  // GPS tracking — starts immediately after FST login on a mobile device.
  //
  // Desktop view never needs geolocation: FST users normally work from
  // mobile, and the desktop dashboards (e.g. Migration > For
  // Calling/Scheduling) do not consume `myLocation`. Triggering a browser
  // geolocation permission prompt and an SSE-style watchPosition on the
  // desktop just slows the initial page load and shows an unwanted
  // permission banner. Gate the hook on a real mobile viewport so:
  //   - Desktop (any role): no permission prompt, no watchPosition,
  //     no /fst/location POSTs.
  //   - Mobile FST / FST Migration: existing behavior preserved.
  //
  // The check uses `window.matchMedia` directly (instead of pulling in the
  // useIsMobile hook) so a viewport resize re-evaluates without forcing the
  // entire AuthProvider to re-render on every breakpoint change.
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobileViewport(e.matches);
    mql.addEventListener?.("change", handler);
    return () => mql.removeEventListener?.("change", handler);
  }, []);
  const fstLocationEnabled = !!(user && (isFST || isFSTMig) && isMobileViewport);
  const { myLocation, locationError: fstLocationError, permissionState: fstLocationPermission } = useFSTLocation(fstLocationEnabled);

  // Whether this user should see the NI (job orders) mobile dashboard at /m
  const canAccessNIDashboard = hasPerm("jo.view_detail") || hasPerm("jo.complete") || hasPerm("jo.reschedule") || hasPerm("jo.cancel") || isSalesAgent;

  // Permission-based access helpers (replace hardcoded role checks)
  const canAccessXlsx       = hasPerm("xlsx.view") || hasPerm("page.xlsx_imports");
  const canImportXlsx       = hasPerm("xlsx.import");
  const canPerformLifecycle  = hasPerm("jo.complete") || hasPerm("jo.reschedule") || hasPerm("jo.cancel");
  const canActivateStaging   = hasPerm("xlsx.activate");
  const canEditJO            = hasPerm("jo.edit");
  const canDeleteJO          = hasPerm("jo.delete");
  const canViewDetail        = hasPerm("jo.view_detail");

  // Page access
  const canAccessDashboard       = hasPerm("page.dashboard");
  const canAccessManageAccounts = hasPerm("page.manage_accounts");
  const canAccessJoRegistry     = hasPerm("page.jo_registry");

  // User management
  const canViewUsers       = hasPerm("users.view");
  const canCreateUsers     = hasPerm("users.create");
  const canChangePassword  = hasPerm("users.change_password");
  const canDeactivateUser  = hasPerm("users.deactivate");

  // Role management
  const canViewRoles   = hasPerm("roles.view");
  const canCreateRoles = hasPerm("roles.create");
  const canEditRoles   = hasPerm("roles.edit");
  const canDeleteRoles = hasPerm("roles.delete");
  const canAssignRoles = hasPerm("roles.assign");

  // Database Backups — visible to admins always, or any role granted the permission
  const canAccessDatabaseBackups = isAdmin || hasPerm("page.database_backups");

  // XLSX granular
  const canEditXlsx   = hasPerm("xlsx.edit");
  const canCancelXlsx = hasPerm("xlsx.cancel");
  const canDeleteXlsx = hasPerm("xlsx.delete");

  // Chat
  const canViewChat = hasPerm("chat.view");
  const canPostChat = hasPerm("chat.post");

  // Sessions
  const canViewLoginActivity = hasPerm("sessions.view");
  const canManageSessions = hasPerm("sessions.manage");

  // Reports
  const canAccessReports = hasPerm("page.reports");
  const canViewContractorReport = hasPerm("reports.daily_contractor");
  const canViewMunicipalityReport = hasPerm("reports.output_municipality");
  const canAccessFSTReport = hasPerm("reports.fst_daily_output");
  const canViewScorecardAllCompanies = hasPerm("reports.scorecard_all_companies");

  // NI
  const canAccessNiDashboard = hasPerm("page.ni_dashboard");
  const canEditBlankJoNumber = hasPerm("ni.edit_blank_jo_number");

  // Migration
  const canAccessMigration = hasPerm("page.migration");
  const canAccessMigrationDashboard = hasPerm("page.migration_dashboard");
  const canMigrationImport = hasPerm("migration.import");
  const canMigrationTag = hasPerm("migration.tag");
  const canMigrationEdit = hasPerm("migration.edit");
  const canViewMigrationUploaded = hasPerm("migration.view_uploaded");
  const canViewMigrationDispatch = hasPerm("migration.view_dispatch");
  const canViewMigrationVisited = hasPerm("migration.view_visited");
  const canViewMigrationCalling = hasPerm("migration.view_calling");
  const canAccessPublicMigrationRequests = hasPerm("page.public_migration_requests");

  // NAP Locator — also gated by company restriction on the role
  const canAccessNapLocator = hasPerm("page.nap_locator") && (() => {
    const allowed = user?.nap_locator_companies;
    if (!allowed || allowed.length === 0) return true; // empty = all companies
    return !!user?.company_id && allowed.includes(user.company_id);
  })();

  /**
   * Returns true if the current user may edit the given job-order field key.
   * Uses the permissions array: field.edit.<field_name>
   */
  const canEditField = useCallback(
    (key) => hasPerm(`field.edit.${key}`),
    [hasPerm]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        mustChangePassword,
        clearMustChangePassword,
        // Core permission checker
        hasPerm,
        permissions,
        // Legacy role flags
        isAdmin,
        isSupervisor,
        isQA,
        isSA,
        isCCA,
        isViewer,
        isSalesAgent,
        isFST,
        isMobileRole,
        canAccessNIDashboard,
        // FST GPS location
        myLocation,
        fstLocationError,
        fstLocationPermission,
        // Permission helpers
        canAccessXlsx,
        canImportXlsx,
        canPerformLifecycle,
        canActivateStaging,
        canEditJO,
        canDeleteJO,
        canViewDetail,
        canEditField,
        // Page access
        canAccessDashboard,
        canAccessManageAccounts,
        canAccessJoRegistry,
        // User management
        canViewUsers,
        canCreateUsers,
        canChangePassword,
        canDeactivateUser,
        // Role management
        canViewRoles,
        canCreateRoles,
        canEditRoles,
        canDeleteRoles,
        canAssignRoles,
        // Database Backups
        canAccessDatabaseBackups,
        // XLSX
        canEditXlsx,
        canCancelXlsx,
        canDeleteXlsx,
        // Chat
        canViewChat,
        canPostChat,
        // Sessions
        canViewLoginActivity,
        canManageSessions,
        // Reports
        canAccessReports,
        canViewContractorReport,
        canViewMunicipalityReport,
        canAccessFSTReport,
        canViewScorecardAllCompanies,
        // NI
        canAccessNiDashboard,
        canEditBlankJoNumber,
        // Migration
        canAccessMigration,
        canAccessMigrationDashboard,
        canMigrationImport,
        canMigrationTag,
        canMigrationEdit,
        canViewMigrationUploaded,
        canViewMigrationDispatch,
        canViewMigrationVisited,
        canViewMigrationCalling,
        canAccessPublicMigrationRequests,
        // NAP Locator
        canAccessNapLocator,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
